import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerModal } from '../Modal'
import PerText from '../Text'
import { KudosCompanyCountryVisibilityDto, KudosCompanyCountryVisibilityFilterInput } from '../../../types'
import { deleteKudosCompanyCountryVisibility } from '../../../graphql/kudos/useKudosMutations'
import { showNotification } from '../Notification/notification'

export interface DeleteKudosCompanyCountryVisibilityModalProps {
  visible: boolean
  onClose: () => void
  kudosCompanyCountryVisibilityDto: KudosCompanyCountryVisibilityDto | null
  filters: KudosCompanyCountryVisibilityFilterInput
  resetFiltersAfterVisibilityChange: () => void
}

export const DeleteKudosCompanyCountryVisibilityModal: FC<DeleteKudosCompanyCountryVisibilityModalProps> = ({
  visible,
  onClose,
  kudosCompanyCountryVisibilityDto,
  filters,
  resetFiltersAfterVisibilityChange,
}) => {
  const { t } = useTranslation()

  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string>('')

  const handleDelete = async () => {
    if (!kudosCompanyCountryVisibilityDto) return

    setIsLoading(true)
    setErrorMessage('')

    const result = await deleteKudosCompanyCountryVisibility(kudosCompanyCountryVisibilityDto.id, filters)

    if (!!result.data && !result.errors) {
      resetAndClose()
      resetFiltersAfterVisibilityChange()
      showNotification({
        type: 'success',
        message: t('kudos.notifications.deleteCompanyCountryVisibility.successTitle'),
        description: t('kudos.notifications.deleteCompanyCountryVisibility.successDescription'),
      })
    } else {
      const errorMessage =
        result.errors?.[0]?.extensions?.validationErrors?.[0]?.message || t('kudos.settings.deleteCompanyCountryVisibilityModal.error')
      setErrorMessage(errorMessage)
      setIsLoading(false)
    }
  }

  const resetAndClose = () => {
    setErrorMessage('')
    setIsLoading(false)
    onClose()
  }

  if (!kudosCompanyCountryVisibilityDto) return null

  return (
    <PerModal
      title={{ heading: t('kudos.settings.deleteCompanyCountryVisibilityModal.title') }}
      size="medium"
      centered
      visible={visible}
      onCancel={resetAndClose}
      footer={{
        cancelBtn: {
          onClick: resetAndClose,
        },
        confirmBtn: {
          onClick: handleDelete,
          disabled: isLoading || !!errorMessage,
          isProcessing: { active: isLoading },
        },
      }}
    >
      <>
        <PerText fontSize="m" fontWeight="bold" mb="xxs">
          {t(`kudos.settings.deleteCompanyCountryVisibilityModal.disclaimer1`)}
        </PerText>
        <PerText fontSize="m">{t(`kudos.settings.deleteCompanyCountryVisibilityModal.disclaimer2`)}</PerText>
      </>

      {errorMessage && (
        <PerText color="error" fontSize="m" mt="m">
          {errorMessage}
        </PerText>
      )}
    </PerModal>
  )
}

export default DeleteKudosCompanyCountryVisibilityModal
